import dayjs, { Dayjs } from "dayjs";

export interface StudyTrackerEntry {
    entry_id: string;
    profile_id: string;
    created_at: string;
    submitted_at: string | null;
    study_number: number;
}

export interface DayStats {
    date: string; // YYYY-MM-DD format
    total: number;
    completed: number;
    completedStudyNumbers: number[];
    incompleteStudyNumbers: number[];
}

export function formatStudyNumber(studyNumber: number): string {
    return `SeaGM${String(studyNumber).padStart(2, "0")}`;
}

/**
 * Groups entries by date (using created_at date, ignoring time)
 */
export function groupEntriesByDate(entries: StudyTrackerEntry[]): Map<string, StudyTrackerEntry[]> {
    const grouped = new Map<string, StudyTrackerEntry[]>();

    for (const entry of entries) {
        const dateKey = dayjs(entry.created_at).format("YYYY-MM-DD");

        if (!grouped.has(dateKey)) {
            grouped.set(dateKey, []);
        }

        grouped.get(dateKey)!.push(entry);
    }

    return grouped;
}

/**
 * Calculates completion stats for a specific day
 */
export function calculateDayStats(dateEntries: StudyTrackerEntry[], allStudyNumbers: number[]): DayStats {
    // Get unique study numbers that have entries for this date
    const studyNumbersWithEntries = new Set(dateEntries.map((entry) => entry.study_number));

    // Total enrolled participants
    const total = allStudyNumbers.length;

    // Count how many entries were completed (have submitted_at)
    const completedEntries = dateEntries.filter((entry) => entry.submitted_at !== null);
    const completed = completedEntries.length;

    // Get study numbers that completed
    const completedStudyNumbers = Array.from(new Set(completedEntries.map((entry) => entry.study_number))).sort(
        (a, b) => a - b
    );

    // Get study numbers that didn't complete (have entry but no submitted_at)
    const incompleteStudyNumbers = Array.from(
        new Set(dateEntries.filter((entry) => entry.submitted_at === null).map((entry) => entry.study_number))
    ).sort((a, b) => a - b);

    // For study numbers that don't have entries at all, we need to include them as incomplete
    // This handles the case where an entry wasn't created for a participant
    const studyNumbersWithoutEntries = allStudyNumbers.filter((studyNum) => !studyNumbersWithEntries.has(studyNum));

    const allIncomplete = [...incompleteStudyNumbers, ...studyNumbersWithoutEntries].sort((a, b) => a - b);

    return {
        date: dayjs(dateEntries[0]?.created_at).format("YYYY-MM-DD"),
        total,
        completed,
        completedStudyNumbers,
        incompleteStudyNumbers: allIncomplete,
    };
}

/**
 * Extracts all unique study numbers from entries
 */
export function getAllStudyNumbers(entries: StudyTrackerEntry[]): number[] {
    const studyNumbers = new Set(entries.map((entry) => entry.study_number));
    return Array.from(studyNumbers).sort((a, b) => a - b);
}

/**
 * Gets stats for a specific date
 */
export function getStatsForDate(date: Dayjs, allDaysStats: DayStats[]): DayStats | null {
    const dateKey = date.format("YYYY-MM-DD");
    return allDaysStats.find((stats) => stats.date === dateKey) || null;
}
